import { Box } from 'src/design/atoms/Box';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Table } from 'src/design/molecules/Table';
import { TextInput } from 'src/design/atoms/TextInput';
import { DateInput } from 'src/design/atoms/DateInput';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { formatDateTime } from 'src/utils';
import { nanoid } from 'nanoid';
import React from 'react';
import { CcAdjustmentHistoryDto, UserDto } from 'src/api/FiretrolApiClient';
import { useCtcAdjustmentHistory } from 'src/api/queries/ctc/useCtcAdjustmentHistory';
import { UserSelect } from 'src/app/selectors/UserSelect';
import { Currency } from 'src/design/atoms/Currency';
import { getCategoryByCode } from 'src/routes/CTC/ViewCTCJob/constants';

dayjs.extend(isBetween);

interface HistoryDialogProps {
  jobId: string;
  open: boolean;
  onClose: () => void;
}

const headerBg = '#f3f3f3';

export const HistoryDialog: React.FC<HistoryDialogProps> = ({
  jobId,
  open,
  onClose,
}) => {
  const { data, isLoading: loading } = useCtcAdjustmentHistory(jobId, open);
  const items = React.useMemo<CcAdjustmentHistoryDto[]>(
    () => data ?? [],
    [data],
  );
  const [filteredItems, setFilteredItems] = React.useState<
    CcAdjustmentHistoryDto[]
  >([]);
  const [filteredUser, setFilteredUser] = React.useState<UserDto | null>(null);
  const [filteredCode, setFilteredCode] = React.useState<string>('');
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);

  React.useEffect(() => {
    if (filteredUser || filteredCode.length > 0 || (startDate && endDate)) {
      const filtered = items.filter((item) => {
        let userResult = true;
        let dateResult = true;
        let codeResult = true;
        if (filteredUser) {
          if (item.createdBy?.id !== filteredUser.id) {
            userResult = false;
          }
        }
        if (filteredCode.length > 0) {
          const exists = !!item.adjustments?.find((i) => {
            return i.costCode
              ?.trim()
              .toLowerCase()
              .includes(filteredCode.trim().toLowerCase());
          });
          if (!exists) codeResult = false;
        }
        if (startDate && endDate) {
          const is =
            dayjs(item.createdAt).isBetween(dayjs(startDate), dayjs(endDate)) ||
            dayjs(item.createdAt).isSame(dayjs(startDate), 'day') ||
            dayjs(item.createdAt).isSame(dayjs(endDate), 'day');

          if (!is) dateResult = false;
        }
        return codeResult && dateResult && userResult;
      });
      setFilteredItems(filtered);
    } else {
      setFilteredItems(items);
    }
  }, [filteredUser, filteredCode, startDate, endDate, items]);

  const renderItems = () => {
    if (items.length === 0) {
      return <p>No Adjustments have been created</p>;
    }
    if (filteredItems.length === 0) {
      return <p>No Adjustments match your filters</p>;
    }
    return filteredItems.map((item) => {
      const hasFilter = filteredItems.length !== items.length;
      return (
        <div key={item.id}>
          <p className="fs-16">
            <b>{item.createdBy?.fullName}</b> updated the following on{' '}
            <b>{formatDateTime(item.createdAt)}</b>:
          </p>
          <div>
            {item.revChanged && (
              <p className="fs-16">
                <b>
                  Adjusted Revenue from{' '}
                  {<Currency precise value={item.previousAdjustedRev} />} to{' '}
                  {<Currency precise value={item.adjustedRev} />}
                </b>
              </p>
            )}
            {item.adjustments.length > 0 && (
              <Table.ScrollContainer
                minWidth={650}
                style={{ marginBottom: 20 }}
              >
                <Table className="header-table">
                  <Table.Thead>
                    <Table.Tr style={{ backgroundColor: headerBg }}>
                      <Table.Th ta="left">Cost Code</Table.Th>
                      <Table.Th ta="center">Hours</Table.Th>
                      <Table.Th ta="center">Rate</Table.Th>
                      <Table.Th ta="center">CTC</Table.Th>
                      <Table.Th ta="center">C@C</Table.Th>
                      <Table.Th ta="center">Over / Under</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {item.adjustments?.map((row) => {
                      const category =
                        getCategoryByCode(row.costCode) || 'none';
                      if (hasFilter) {
                        if (filteredCode.length > 0) {
                          if (
                            !row.costCode
                              .trim()
                              .toLowerCase()
                              .includes(filteredCode.trim().toLowerCase())
                          ) {
                            return null;
                          }
                        }
                      }
                      return (
                        <Table.Tr key={nanoid()}>
                          <Table.Td style={{ width: 300 }}>
                            {row.description} - {row.costCode}
                          </Table.Td>
                          <Table.Td ta="center">
                            {category === 'Labor' ? row.hours : '--'}
                          </Table.Td>
                          <Table.Td ta="center">
                            {category === 'Labor' ? (
                              <Currency precise value={row.rate} />
                            ) : (
                              '--'
                            )}
                          </Table.Td>
                          <Table.Td ta="center">
                            {<Currency precise value={row.ctc} />}
                          </Table.Td>
                          <Table.Td ta="center">
                            {<Currency precise value={row.costAtCompletion} />}
                          </Table.Td>
                          <Table.Td ta="center">
                            {<Currency precise value={row.overUnder} />}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </div>
        </div>
      );
    });
  };

  const filterUserData = (options: UserDto[]) => {
    return options.filter(
      (o) => !!items.find((adjustment) => adjustment.createdBy?.id === o.id),
    );
  };

  return (
    <BaseModal
      opened={open}
      onClose={onClose}
      title="Adjustment History"
      size="xl"
    >
      <Box p="md">
        {loading && (
          <div style={{ textAlign: 'center', paddingTop: 50 }}>
            <Loader />
          </div>
        )}
        <Box mb="lg" pb="md" style={{ borderBottom: '1px solid #e9e9e9' }}>
          <Grid>
            <Grid.Col span={6}>
              <label>User</label>
              <UserSelect
                dataFilter={filterUserData}
                hideLabel
                value={filteredUser}
                onChange={(user) => setFilteredUser(user)}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <label>Cost Code</label>
              <TextInput
                value={filteredCode}
                onChange={(e) => {
                  setFilteredCode(e.currentTarget.value);
                }}
                placeholder="Cost Code"
              />
            </Grid.Col>
          </Grid>
          <Grid mt="sm">
            <Grid.Col span={6}>
              <DateInput
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                placeholder="Start"
                clearable
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <DateInput
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                placeholder="End"
                clearable
              />
            </Grid.Col>
          </Grid>
        </Box>
        {!loading && (
          <Box p="md" pt={0} style={{ height: 500, overflow: 'auto' }}>
            {renderItems()}
          </Box>
        )}

        <Group justify="flex-end" mt="lg">
          <BasicButton variant="outline" onClick={onClose}>
            Close
          </BasicButton>
        </Group>
      </Box>
    </BaseModal>
  );
};
